/**
 * <holiday-snowfall> — canvas snow that covers its parent section.
 *
 * Plain JS take on react-snowfall: a fixed pool of flakes, each with its own
 * radius, speed, drift and opacity, redrawn on a capped frame rate. The
 * canvas sits absolutely over the section and ignores pointer events, so it
 * never gets in the way of the content.
 *
 * Attributes (all optional):
 *   data-count   number of flakes at 1440 x 1700; scales with the section area
 *   data-color   fill colour (any CSS colour)
 *   data-speed   fall speed multiplier (1 = design pace)
 *   data-radius  "min,max" flake radius in px
 *   data-wind    horizontal drift multiplier
 *
 * Cheap by design: flakes are blits of one pre-rendered sprite instead of
 * per-frame path fills, the loop runs at ~30fps (plenty for slow snow), the
 * backing store is capped at 1.5x, and nothing runs until the section is
 * about to scroll into view. The pool starts at a fifth of the count and
 * ramps to the full amount over a few seconds, each late flake fading in.
 * It pauses off-screen or on a hidden tab, and draws a single still frame
 * when the visitor prefers reduced motion.
 */
const FRAME_INTERVAL = 1000 / 30;
const SPRITE_SIZE = 32;
// Share of the flake count present on the first frame, how long the rest
// takes to arrive, and how long each late flake takes to fade in.
const RAMP_START = 0.2;
const RAMP_SECONDS = 4;
const FADE_SECONDS = 1;

class HolidaySnowfall extends HTMLElement {
  connectedCallback() {
    this.canvas = document.createElement('canvas');
    this.canvas.setAttribute('aria-hidden', 'true');
    this.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d', { alpha: true, desynchronized: true });

    this.count = Number(this.dataset.count) || 150;
    this.color = this.dataset.color || '#ffffff';
    this.speed = Number(this.dataset.speed) || 1;
    this.wind = Number(this.dataset.wind) || 1;
    const [rMin, rMax] = (this.dataset.radius || '1.5,4').split(',').map(Number);
    this.radius = [rMin || 1.5, rMax || 4];

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.flakes = [];
    this.running = false;
    this.visible = false;
    this.frame = null;
    this.lastTime = 0;
    this.lastDraw = 0;
    this.sprite = this.makeSprite();

    this.resize = this.resize.bind(this);
    this.tick = this.tick.bind(this);
    this.onVisibility = this.onVisibility.bind(this);

    // Everything else waits for the section to approach the viewport, so a
    // page that never scrolls here pays nothing beyond this observer.
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        this.visible = entries.some((entry) => entry.isIntersecting);
        if (this.visible) {
          this.ensureSized();
          this.start();
        } else {
          this.stop();
        }
      },
      { rootMargin: '200px' }
    );
    this.intersectionObserver.observe(this);

    document.addEventListener('visibilitychange', this.onVisibility);
  }

  disconnectedCallback() {
    this.stop();
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  onVisibility() {
    document.hidden ? this.stop() : this.visible && this.start();
  }

  ensureSized() {
    if (this.resizeObserver) return;
    this.resizeObserver = new ResizeObserver(this.resize);
    this.resizeObserver.observe(this);
    this.resize();
  }

  /* One soft-edged disc, drawn once; every flake is a scaled blit of it. */
  makeSprite() {
    const sprite = document.createElement('canvas');
    sprite.width = sprite.height = SPRITE_SIZE;
    const ctx = sprite.getContext('2d');
    const half = SPRITE_SIZE / 2;
    const gradient = ctx.createRadialGradient(half, half, 0, half, half, half);
    gradient.addColorStop(0, this.color);
    gradient.addColorStop(0.8, this.color);
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(half, half, half, 0, Math.PI * 2);
    ctx.fill();
    return sprite;
  }

  resize() {
    const { width, height } = this.getBoundingClientRect();
    if (!width || !height) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    this.width = width;
    this.height = height;
    this.canvas.width = Math.round(width * dpr);
    this.canvas.height = Math.round(height * dpr);
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Keep the density the design shows at 1440 x 1700 regardless of how tall
    // the section ends up with more or fewer blocks.
    this.target = Math.round((this.count * width * height) / (1440 * 1700));

    // Ramp up instead of spawning the whole pool at once: start with a light
    // dusting and let `update` grow it to the full count over RAMP_SECONDS,
    // so the first frames (right when the page is busiest) are the cheapest.
    // Reduced motion gets the full still frame straight away.
    const initial = this.reducedMotion.matches ? this.target : Math.round(this.target * RAMP_START);
    while (this.flakes.length < initial) this.flakes.push(this.makeFlake(true, true));
    this.flakes.length = Math.min(this.flakes.length, this.target);

    if (this.reducedMotion.matches) this.draw();
  }

  makeFlake(anywhere, settled) {
    const [rMin, rMax] = this.radius;
    const r = rMin + Math.random() * (rMax - rMin);
    return {
      x: Math.random() * this.width,
      y: anywhere ? Math.random() * this.height : -r * 2,
      r,
      // Bigger flakes read as closer, so they fall a little faster.
      vy: (0.4 + (r / rMax) * 0.8) * 40,
      vx: (Math.random() - 0.5) * 20,
      phase: Math.random() * Math.PI * 2,
      alpha: 0.35 + Math.random() * 0.5,
      // Flakes added mid-air fade in so the ramp never pops; the ones that
      // exist from the first frame are already fully visible.
      fade: settled ? 1 : 0,
    };
  }

  start() {
    if (this.running || this.reducedMotion.matches || document.hidden) return;
    this.running = true;
    this.lastTime = performance.now();
    this.lastDraw = 0;
    this.frame = requestAnimationFrame(this.tick);
  }

  stop() {
    this.running = false;
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  tick(now) {
    if (!this.running) return;
    this.frame = requestAnimationFrame(this.tick);

    // Skip frames down to ~30fps; the physics still integrate real time, so
    // the pace is identical on 60Hz and 120Hz screens.
    if (now - this.lastDraw < FRAME_INTERVAL) return;
    this.lastDraw = now;

    // Clamp so a background tab coming back doesn't teleport every flake.
    const dt = Math.min((now - this.lastTime) / 1000, 0.1);
    this.lastTime = now;
    this.update(dt, now / 1000);
    this.draw();
  }

  update(dt, t) {
    const { width, height } = this;

    // Grow the pool towards the target at a steady rate. New flakes appear
    // anywhere in the band (fading in) rather than only at the top, so the
    // whole section fills evenly instead of in a descending front.
    if (this.flakes.length < this.target) {
      this.spawnDebt = (this.spawnDebt || 0) + (this.target * (1 - RAMP_START) * dt) / RAMP_SECONDS;
      while (this.spawnDebt >= 1 && this.flakes.length < this.target) {
        this.flakes.push(this.makeFlake(true, false));
        this.spawnDebt -= 1;
      }
    }

    for (let i = 0; i < this.flakes.length; i++) {
      const flake = this.flakes[i];
      if (flake.fade < 1) flake.fade = Math.min(1, flake.fade + dt / FADE_SECONDS);
      flake.y += flake.vy * this.speed * dt;
      flake.x += (flake.vx + Math.sin(t + flake.phase) * 15) * this.wind * dt;

      if (flake.y - flake.r > height) {
        this.flakes[i] = this.makeFlake(false, true);
      } else if (flake.x < -flake.r) {
        flake.x = width + flake.r;
      } else if (flake.x > width + flake.r) {
        flake.x = -flake.r;
      }
    }
  }

  draw() {
    const { ctx, width, height, sprite } = this;
    ctx.clearRect(0, 0, width, height);
    for (const flake of this.flakes) {
      const size = flake.r * 2;
      ctx.globalAlpha = flake.alpha * flake.fade;
      ctx.drawImage(sprite, flake.x - flake.r, flake.y - flake.r, size, size);
    }
    ctx.globalAlpha = 1;
  }
}

if (!customElements.get('holiday-snowfall')) {
  customElements.define('holiday-snowfall', HolidaySnowfall);
}
